import React, { ReactNode, useContext } from 'react';

import Pin from '../Pin/Pin';

import { bemHelper } from '../../../helpers';
import { hasCheckbox } from '../helpers/helpers';
import getGlobalProps from '../../../helpers/constants/global-props';

import TableContext from '../TableContext/TableContext';
import useOffsetLeft from '../hooks/useOffsetLeft';

export interface TableHeaderProps {
  /** TableHeader contents */
  children?: any;
  /** Click callback */
  onClick?: () => void;
  /** Modifier for sortable headers */
  sortable?: boolean;
  /** Modifier for indents */
  indent?: 1 | 2 | 3 | 4;
  /** Modifier for text placemente in relation the the sorting arrows */
  right?: boolean;
  /** Modifier for checkbox header. This prop can be omitted if
   * you are passing an Ink Checkbox Component as the header label */
  checkbox?: boolean;
  /** Modifier header ordering */
  order?: 'ASC' | 'DESC';
  /** HTML colspan attribute */
  colSpan?: number;
  /** Modifier to allow header texts to wrap to more than one line */
  wrapText?: boolean;
  /** Slot that you can use to pass a Help tooltip component
   * with extra information about the header */
  help?: ReactNode;
  /** Freezes the header to the left when the table is scrolled. You should **always** pass this prop to the
   * **header and all the cells in the same column**. If you want to freeze **more than one column** in a table,
   * make sure you freeze them **in a sequence**. From the first column to the last one you want to freeze,
   * without skipping any cell or header in between. */
  freeze?: boolean;
  /** @ignore */
  toggleable?: boolean;
  /** @ignore */
  ariaLabel?: string;
}

const globalProps = getGlobalProps({ sunset: true, inkSass: true });

function TableHeader(props: TableHeaderProps) {
  const { mini } = useContext(TableContext);
  const [offsetLeft, cellTagRef] = useOffsetLeft(props.freeze);
  const {
    sortable,
    indent,
    right,
    order,
    checkbox,
    onClick,
    colSpan,
    wrapText,
    ariaLabel,
    help,
    toggleable,
    freeze,
  } = props;

  const isClickable = sortable || toggleable;

  function defineClassName() {
    const b = bemHelper();

    return b('ink-table--header', {
      mini,
      right,
      checkbox: checkbox || hasCheckbox(props),
      [`indent-${indent}`]: Boolean(indent),
      wrap: wrapText,
      twiddle: toggleable,
      freeze,
    });
  }

  function handleClick() {
    if (onClick) onClick();
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' || e.keyCode === 13) {
      handleClick();
    }
  }

  return (
    <th
      ref={cellTagRef}
      className={defineClassName()}
      colSpan={colSpan}
      onKeyDown={handleKeyDown}
      style={props.freeze ? { left: offsetLeft } : undefined}
      {...globalProps}
    >
      {right && help}
      {isClickable && (
        <>
          {toggleable && (
            <Pin
              onClick={handleClick}
              ariaLabel={ariaLabel}
              noCarat
              order={order}
              right={right}
              customIcon={props.children}
            />
          )}
          {!toggleable && (
            <Pin onClick={handleClick} ariaLabel={ariaLabel} order={order} right={right}>
              {props.children}
            </Pin>
          )}
        </>
      )}
      {!isClickable && props.children}
      {!right && help}
    </th>
  );
}

TableHeader.defaultProps = {
  children: null,
  onClick: null,
  sortable: false,
  indent: null,
  right: false,
  checkbox: false,
  order: null,
  colSpan: 1,
  wrapText: false,
  help: undefined,
  freeze: false,
  ariaLabel: null,
};

export default TableHeader;
